import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import API, { SellOutAPI } from '../../services/api';
import { addTab } from '../../tabs/tabsSlice';
import {
  documentTypeLabel,
  documentTypeOptions,
  fiscalStatusColor,
  fiscalStatusLabel,
  fiscalStatusOptions,
  formatDate,
  formatMoney,
  supplierLabel,
} from '../../components/sellout/selloutUtils';

const typeFilterOptions = [{ value: '', label: 'Todos' }, ...documentTypeOptions];
const fiscalFilterOptions = [{ value: '', label: 'Todos' }, ...fiscalStatusOptions];

export default function SellOutDocumentList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [suppliers, setSuppliers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    supplier_id: null,
    document_type: '',
    fiscal_status: '',
    search: '',
  });
  const [snack, setSnack] = useState({
    open: Boolean(location.state?.message),
    severity: 'success',
    message: location.state?.message || '',
  });

  useEffect(() => {
    dispatch(addTab({ label: 'Documentos Sell Out', path: '/sellout/documents' }));
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;
    API.get('suppliers/minimal/', { params: { active: true } })
      .then(({ data }) => {
        if (mounted) setSuppliers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (mounted) setSnack({ open: true, severity: 'error', message: 'No se pudo cargar proveedores.' });
      });
    return () => {
      mounted = false;
    };
  }, []);

  const selectedSupplier = useMemo(
    () => suppliers.find((supplier) => supplier.id === filters.supplier_id) || null,
    [filters.supplier_id, suppliers]
  );

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.supplier_id) params.supplier_id = filters.supplier_id;
      if (filters.document_type) params.document_type = filters.document_type;
      if (filters.fiscal_status) params.fiscal_status = filters.fiscal_status;
      if (filters.search.trim()) params.search = filters.search.trim();
      const { data } = await SellOutAPI.documents.list(params);
      setRows(Array.isArray(data) ? data : data?.results || []);
    } catch {
      setRows([]);
      setSnack({ open: true, severity: 'error', message: 'No se pudieron cargar documentos Sell Out.' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const totals = useMemo(() => rows.reduce((acc, row) => ({
    received: acc.received + Number(row.received_total || 0),
    accepted: acc.accepted + Number(row.accepted_total || 0),
    rejected: acc.rejected + Number(row.rejected_total || 0),
  }), { received: 0, accepted: 0, rejected: 0 }), [rows]);

  const columns = [
    {
      field: 'number',
      headerName: 'Documento',
      minWidth: 150,
      flex: 0.8,
      valueGetter: (value, row) => `${row?.series ? `${row.series}-` : ''}${value || ''}`,
    },
    {
      field: 'supplier',
      headerName: 'Proveedor',
      minWidth: 220,
      flex: 1,
      valueGetter: (value) => supplierLabel(value),
    },
    {
      field: 'document_type',
      headerName: 'Tipo',
      width: 170,
      renderCell: (params) => <Chip size="small" label={documentTypeLabel(params.value)} />,
    },
    {
      field: 'date',
      headerName: 'Fecha',
      width: 120,
      valueGetter: (value) => formatDate(value),
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value) => formatMoney(value),
    },
    {
      field: 'received_total',
      headerName: 'Recibido',
      width: 120,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'fiscal_status',
      headerName: 'Fiscal',
      width: 160,
      renderCell: (params) => (
        <Chip
          size="small"
          label={fiscalStatusLabel(params.value)}
          color={fiscalStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'receive_note',
      headerName: 'Nota entrada',
      width: 140,
      valueGetter: (value) => (value?.number ? `NR ${value.number}` : '-'),
    },
    {
      field: 'linked_invoice',
      headerName: 'Factura vinculada',
      width: 160,
      valueGetter: (value) => (value?.number ? `${value.series ? `${value.series}-` : ''}${value.number}` : '-'),
    },
  ];

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', width: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Documentos Sell Out</Typography>
          <Typography variant="body2" color="text.secondary">
            Recibido {totals.received} / Aceptado {totals.accepted} / Rechazado {totals.rejected}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshCw size={17} />} onClick={loadDocuments} disabled={loading}>
            Actualizar
          </Button>
          <Button variant="contained" startIcon={<Plus size={17} />} onClick={() => navigate('/sellout/documents/create')}>
            Ingresar documento
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.5 }, borderRadius: 1, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
          <Autocomplete
            options={suppliers}
            value={selectedSupplier}
            onChange={(_, supplier) => setFilters((prev) => ({ ...prev, supplier_id: supplier?.id || null }))}
            getOptionLabel={supplierLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => <TextField {...params} size="small" label="Proveedor" />}
            sx={{ minWidth: { md: 280 }, flex: 1 }}
          />
          <TextField
            select
            size="small"
            label="Tipo"
            value={filters.document_type}
            onChange={(event) => setFilters((prev) => ({ ...prev, document_type: event.target.value }))}
            sx={{ minWidth: { md: 190 } }}
          >
            {typeFilterOptions.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Estado fiscal"
            value={filters.fiscal_status}
            onChange={(event) => setFilters((prev) => ({ ...prev, fiscal_status: event.target.value }))}
            sx={{ minWidth: { md: 180 } }}
          >
            {fiscalFilterOptions.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Buscar"
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            InputProps={{ startAdornment: <Search size={17} style={{ marginRight: 8 }} /> }}
            sx={{ minWidth: { md: 240 }, flex: 1 }}
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ height: 560, borderRadius: 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f7f8fa' },
          }}
        />
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
